import { useEffect, useRef, useState } from 'react';
import {
    DEVICE_LABELS,
    DEVICES_REQUIRING_SELECTION,
    fieldErrors,
    identificationLabel,
    startAuthentikLogin,
    submitAuthentikStep,
} from './authentikFlow';

export { AuthentikLoginForm };

// Renders the Authentik login flow on the dashboard's own login page.
// onAuthenticated(user) is called with the /authenticate-style payload;
// onFallback() when the sign-in must continue on Authentik's own page.
function AuthentikLoginForm({ onAuthenticated, onFallback, onBusyChange }) {
    const [challenge, setChallenge] = useState(null);
    const [phase, setPhase] = useState('loading'); // loading | ready | submitting | failed
    const [formError, setFormError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [uid, setUid] = useState('');
    const [password, setPassword] = useState('');
    const [code, setCode] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [deviceClass, setDeviceClass] = useState(null);
    const focusRef = useRef(null);
    // Latest callbacks, so the flow is not restarted when the parent re-renders.
    const callbacks = useRef({ onAuthenticated, onFallback });
    callbacks.current = { onAuthenticated, onFallback };
    // Only the newest request may update the form; older responses are dropped.
    const requestSeq = useRef(0);

    useEffect(() => {
        onBusyChange?.(phase === 'submitting' || phase === 'loading');
    }, [phase, onBusyChange]);

    function applyResult(result, previousComponent) {
        switch (result.status) {
            case 'authenticated': {
                const { status, ...user } = result;
                callbacks.current.onAuthenticated(user);
                return;
            }
            case 'fallback':
                callbacks.current.onFallback();
                return;
            case 'challenge': {
                const next = result.challenge;
                if (next.component !== previousComponent) {
                    setPassword('');
                    setCode('');
                    setShowPassword(false);
                }
                if (next.component === 'ak-stage-authenticator-validate') {
                    const devices = next.device_challenges || [];
                    setDeviceClass((current) => next.selected_device_class
                        || current
                        || (devices.length === 1 && !DEVICES_REQUIRING_SELECTION.has(devices[0].device_class)
                            ? devices[0].device_class
                            : null));
                } else {
                    setDeviceClass(null);
                }
                setChallenge(next);
                setFormError(null);
                setPhase('ready');
                return;
            }
            case 'expired':
                restart('Your sign-in timed out. Please start again.');
                return;
            case 'denied':
                setChallenge(null);
                setFormError(result.error || 'Access denied.');
                setPhase('failed');
                return;
            default:
                setFormError(result.error || 'Sign-in failed. Please try again.');
                setPhase((current) => (current === 'loading' ? 'failed' : 'ready'));
        }
    }

    async function restart(message = null) {
        const seq = ++requestSeq.current;
        setPhase('loading');
        setChallenge(null);
        setFormError(null);
        setNotice(message);
        setUid('');
        setPassword('');
        setCode('');
        setDeviceClass(null);
        const result = await startAuthentikLogin();
        if (seq === requestSeq.current) {
            applyResult(result, null);
        }
    }

    async function submit(payload) {
        const seq = ++requestSeq.current;
        const previousComponent = challenge.component;
        setPhase('submitting');
        setFormError(null);
        setNotice(null);
        const result = await submitAuthentikStep({ component: previousComponent, ...payload });
        if (seq === requestSeq.current) {
            applyResult(result, previousComponent);
        }
    }

    useEffect(() => {
        restart();
        // Invalidate in-flight responses when unmounting.
        return () => { requestSeq.current += 1; };
        // Start one attempt per mount; restart() only uses stable setters and refs.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (phase === 'ready') {
            focusRef.current?.focus();
        }
    }, [phase, challenge?.component, deviceClass]);

    function handleSubmit(event) {
        event.preventDefault();
        if (!challenge || phase === 'submitting') return;
        switch (challenge.component) {
            case 'ak-stage-identification':
                if (!uid.trim()) {
                    setFormError(`${identificationLabel(challenge.user_fields)} is required`);
                    return;
                }
                submit(challenge.password_fields ? { uid_field: uid.trim(), password } : { uid_field: uid.trim() });
                return;
            case 'ak-stage-password':
                if (!password) {
                    setFormError('Password is required');
                    return;
                }
                submit({ password });
                return;
            case 'ak-stage-authenticator-validate':
                if (!code.trim()) {
                    setFormError('Code is required');
                    return;
                }
                submit({ code: code.trim() });
                return;
            case 'ak-stage-consent':
                submit({});
                return;
            default:
        }
    }

    function selectDevice(device) {
        setCode('');
        setFormError(null);
        if (DEVICES_REQUIRING_SELECTION.has(device.device_class)) {
            submit({ selected_challenge: { device_class: device.device_class, device_uid: device.device_uid } });
        } else {
            setDeviceClass(device.device_class);
        }
    }

    const busy = phase === 'submitting';

    if (phase === 'loading') {
        return (
            <div className="login-flow-loading" role="status">
                <span className="login-spinner" aria-hidden="true"></span>
                Connecting to NETX sign-in…
            </div>
        );
    }

    if (phase === 'failed') {
        return (
            <div className="login-flow">
                {formError && <FormAlert message={formError} />}
                <button type="button" className="login-btn login-btn-primary" onClick={() => restart()}>
                    <span className="material-symbols-outlined" aria-hidden="true">refresh</span>
                    Start again
                </button>
            </div>
        );
    }

    const component = challenge?.component;
    const nonFieldErrors = fieldErrors(challenge, 'non_field_errors');

    return (
        <form className="login-flow" onSubmit={handleSubmit} noValidate>
            {notice && <div className="login-session-message" role="status">{notice}</div>}

            {challenge?.pending_user && component !== 'ak-stage-identification' && (
                <div className="login-user-chip">
                    <span className="material-symbols-outlined" aria-hidden="true">account_circle</span>
                    <span className="login-user-chip-name">{challenge.pending_user}</span>
                    <button type="button" className="login-link" onClick={() => restart()} disabled={busy}>
                        Not you?
                    </button>
                </div>
            )}

            {component === 'ak-stage-identification' && (
                <>
                    <Field
                        id="sso-uid"
                        label={identificationLabel(challenge.user_fields)}
                        icon="person"
                        errors={fieldErrors(challenge, 'uid_field')}
                    >
                        <input
                            ref={focusRef}
                            id="sso-uid"
                            name="username"
                            type={challenge.user_fields?.length === 1 && challenge.user_fields[0] === 'email' ? 'email' : 'text'}
                            autoComplete="username"
                            autoCapitalize="none"
                            spellCheck="false"
                            value={uid}
                            onChange={(event) => setUid(event.target.value)}
                            className="login-input"
                            aria-invalid={fieldErrors(challenge, 'uid_field').length > 0}
                        />
                    </Field>
                    {challenge.password_fields && (
                        <PasswordField
                            value={password}
                            onChange={setPassword}
                            show={showPassword}
                            onToggle={() => setShowPassword((value) => !value)}
                            errors={fieldErrors(challenge, 'password')}
                            recoveryUrl={challenge.recovery_url}
                        />
                    )}
                </>
            )}

            {component === 'ak-stage-password' && (
                <PasswordField
                    inputRef={focusRef}
                    value={password}
                    onChange={setPassword}
                    show={showPassword}
                    onToggle={() => setShowPassword((value) => !value)}
                    errors={fieldErrors(challenge, 'password')}
                    recoveryUrl={challenge.recovery_url}
                />
            )}

            {component === 'ak-stage-authenticator-validate' && (
                <MfaStep
                    challenge={challenge}
                    deviceClass={deviceClass}
                    code={code}
                    onCodeChange={setCode}
                    onSelectDevice={selectDevice}
                    onChooseAnother={() => { setDeviceClass(null); setCode(''); }}
                    inputRef={focusRef}
                    busy={busy}
                />
            )}

            {component === 'ak-stage-consent' && (
                <div className="login-consent">
                    <p className="login-step-title">{challenge.header_text || 'NetX Stats would like to access:'}</p>
                    <ul>
                        {[...(challenge.permissions || []), ...(challenge.additional_permissions || [])].map((permission) => (
                            <li key={permission}>
                                <span className="material-symbols-outlined" aria-hidden="true">check</span>
                                {permission}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {nonFieldErrors.map((message) => <FormAlert key={message} message={message} />)}
            {formError && <FormAlert message={formError} />}

            {(component !== 'ak-stage-authenticator-validate' || deviceClass) && (
                <button ref={component === 'ak-stage-consent' ? focusRef : undefined} type="submit" className="login-btn login-btn-primary" disabled={busy}>
                    {busy
                        ? <span className="login-spinner" aria-hidden="true"></span>
                        : <span className="material-symbols-outlined" aria-hidden="true">{submitIcon(component)}</span>}
                    {submitLabel(challenge)}
                </button>
            )}
        </form>
    );
}

function submitLabel(challenge) {
    switch (challenge?.component) {
        case 'ak-stage-identification':
            return challenge.password_fields ? 'Sign in' : 'Continue';
        case 'ak-stage-password':
            return 'Sign in';
        case 'ak-stage-authenticator-validate':
            return 'Verify';
        case 'ak-stage-consent':
            return 'Continue';
        default:
            return 'Continue';
    }
}

function submitIcon(component) {
    switch (component) {
        case 'ak-stage-identification':
            return 'arrow_forward';
        case 'ak-stage-authenticator-validate':
            return 'verified_user';
        default:
            return 'login';
    }
}

function Field({ id, label, icon, errors = [], aside, children }) {
    return (
        <div className="login-field">
            <div className="login-field-header">
                <label className="login-label" htmlFor={id}>{label}</label>
                {aside}
            </div>
            <div className="login-input-wrap">
                {icon && <span className="material-symbols-outlined login-input-icon" aria-hidden="true">{icon}</span>}
                {children}
            </div>
            {errors.map((message) => (
                <div key={message} className="login-field-error">{message}</div>
            ))}
        </div>
    );
}

function PasswordField({ inputRef, value, onChange, show, onToggle, errors, recoveryUrl }) {
    return (
        <Field
            id="sso-password"
            label="Password"
            icon="lock"
            errors={errors}
            aside={recoveryUrl && (
                <a className="login-link" href={recoveryUrl}>Forgot password?</a>
            )}
        >
            <input
                ref={inputRef}
                id="sso-password"
                name="password"
                type={show ? 'text' : 'password'}
                autoComplete="current-password"
                value={value}
                onChange={(event) => onChange(event.target.value)}
                className="login-input login-input-with-action"
                aria-invalid={errors.length > 0}
            />
            <button
                type="button"
                className="login-input-action"
                onClick={onToggle}
                aria-label={show ? 'Hide password' : 'Show password'}
                aria-pressed={show}
            >
                <span className="material-symbols-outlined" aria-hidden="true">{show ? 'visibility' : 'visibility_off'}</span>
            </button>
        </Field>
    );
}

function MfaStep({ challenge, deviceClass, code, onCodeChange, onSelectDevice, onChooseAnother, inputRef, busy }) {
    const devices = challenge.device_challenges || [];

    if (!deviceClass) {
        return (
            <div className="login-field">
                <p className="login-step-title">Choose a verification method</p>
                <div className="login-device-list">
                    {devices.map((device) => {
                        const meta = DEVICE_LABELS[device.device_class];
                        return (
                            <button
                                key={`${device.device_class}-${device.device_uid}`}
                                type="button"
                                className="login-device"
                                onClick={() => onSelectDevice(device)}
                                disabled={busy}
                            >
                                <span className="material-symbols-outlined" aria-hidden="true">{meta?.icon || 'password'}</span>
                                {meta?.label || device.device_class}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    }

    const meta = DEVICE_LABELS[deviceClass];
    const numeric = deviceClass !== 'static';
    return (
        <Field
            id="sso-code"
            label="Verification code"
            icon="pin"
            errors={fieldErrors(challenge, 'code')}
            aside={devices.length > 1 && (
                <button type="button" className="login-link" onClick={onChooseAnother} disabled={busy}>
                    Use another method
                </button>
            )}
        >
            <input
                ref={inputRef}
                id="sso-code"
                name="code"
                type="text"
                inputMode={numeric ? 'numeric' : 'text'}
                autoComplete="one-time-code"
                maxLength={numeric ? 8 : 32}
                value={code}
                onChange={(event) => onCodeChange(numeric ? event.target.value.replace(/\D/g, '') : event.target.value)}
                className="login-input login-code-input"
                aria-describedby="sso-code-hint"
                aria-invalid={fieldErrors(challenge, 'code').length > 0}
            />
            <p id="sso-code-hint" className="login-hint">{meta?.hint}</p>
        </Field>
    );
}

function FormAlert({ message }) {
    return (
        <div className="login-alert-error" role="alert">
            <span className="material-symbols-outlined" aria-hidden="true">error</span>
            {message}
        </div>
    );
}

export { Field, FormAlert };
